<script setup>
  const { ageVerificationOverlayActive, bettingContentOptIn, verifyAge, toggleBettingContentOptIn } = useAgeVerification();

  const handleConfirmAge = () => {
    // Track event based on betting content preference
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'age_verification',
        verification_status: bettingContentOptIn.value ? 'age_confirmed_betting_enabled' : 'age_confirmed_betting_disabled',
        verification_result: 'confirmed',
        betting_content_enabled: bettingContentOptIn.value
      });
    }
    verifyAge(true);
  };

  const handleDenyAge = () => {
    // Track age verification rejection
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'age_verification',
        verification_status: 'age_verification_rejected',
        verification_result: 'denied',
        betting_content_enabled: false
      });
    }
    verifyAge(false);
  };
</script>

<!---------------------------------------------------------------------------->

<template>
  <div @wheel.stop class="overlay" :class="{ active: ageVerificationOverlayActive }">
    <Grid full>
      <GridItem :from="4" :to="9">
        <div class="panel">
          <h2>Leeftijdsverificatie</h2>
          <div>
            <p>
              Om te voldoen aan de kansspelregelgeving vragen we je te bevestigen dat je 24 jaar of ouder bent en toegang wilt tot kansspelgerelateerde sportcontent.
            </p>
          </div>

          <div class="checkbox-wrapper">
            <label class="checkbox-label">
              <input
                type="checkbox"
                v-model="bettingContentOptIn"
                @change="toggleBettingContentOptIn(bettingContentOptIn)"
                class="checkbox-input"
              />
              <span class="checkbox-custom"></span>
              <span class="checkbox-text">Toon kansspelgerelateerde sportcontent</span>
            </label>
          </div>

          <div class="button-wrapper">
            <Button @click="handleConfirmAge" class="button confirm" to="javascript:;">
              Ik ben 24 jaar of ouder
            </Button>
            <Button @click="handleDenyAge" class="button deny" outline to="javascript:;">
              Ik ben jonger dan 24 jaar
            </Button>
          </div>

          <div class="fine-print">
            <p>
              Je leeftijd dient naar waarheid te worden ingevuld.
            </p>
          </div>
        </div>
      </GridItem>
    </Grid>
    <div class="background" />
  </div>
</template>

<!---------------------------------------------------------------------------->

<style lang="scss" scoped>
  .overlay {
    position: fixed;
    pointer-events: none;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: $layer-overlay;

    > div {
      height: 100%;
      pointer-events: none;

      :deep(.grid) {
        height: 100%;
        align-items: center;

        > div {
          @include break-max {
            @include column(3, 10);
          }

          @include break-medium {
            @include column(2, 11);
          }

          @include break-small {
            @include column(1, 12);
          }
        }
      }
    }

    .background {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      background: $black;
      opacity: 0;
      @include fade(opacity, 0.21s);
      z-index: -1;
    }

    .panel {
      position: relative;
      display: none;
      max-height: 100vh;
      overflow-y: scroll;
      background-color: $black-2;
      padding: px(72) 7.5vw;

      @include break-small {
        padding: px(56) px(42);
      }

      h2 {
        @include title-l;
        color: $white;
        text-align: center;
        margin-bottom: px(16);
        white-space: nowrap;
        width: 100vw;
        margin-left: -50vw;
        left: 50%;
        position: relative;

        @include break-small {
          font-size: px(28);
        }
      }

      p {
        color: $white;
        text-align: center;
        margin-bottom: px(40);

        a {
          text-decoration: underline;
          @include fade('color', 0.07s);
          @include hover {
            color: $red;
          }
        }
      }

      .checkbox-wrapper {
        display: flex;
        justify-content: center;
        margin-bottom: px(32);

        .checkbox-label {
          display: flex;
          align-items: center;
          cursor: pointer;
          color: $white;
          gap: px(12);

          .checkbox-input {
            display: none;
          }

          .checkbox-custom {
            position: relative;
            width: px(20);
            height: px(20);
            border: 2px solid $white;
            background: transparent;
            border-radius: px(3);
            @include fade(all, 0.1s);

            &::after {
              content: '';
              position: absolute;
              left: px(4);
              top: px(1);
              width: px(6);
              height: px(12);
              border: solid $white;
              border-width: 0 2px 2px 0;
              transform: rotate(45deg);
              opacity: 0;
              @include fade(opacity, 0.1s);
            }
          }

          .checkbox-text {
            user-select: none;
          }

          .checkbox-input:checked + .checkbox-custom {
            background-color: $red;
            border-color: $red;

            &::after {
              opacity: 1;
            }
          }

          @include hover {
            .checkbox-custom {
              border-color: $red;
            }
          }
        }
      }

      .button-wrapper {
        width: px(360);
        display: flex;
        column-gap: px(4);
        margin: 0 auto px(40);

        @include break-small {
          width: 100%;
          flex-direction: column;
          row-gap: px(12);
        }

        .button {
          width: calc(50% - px(2));
          font-size: px(12);
          padding: px(12) px(6);

          @include break-small {
            width: 100%;
            font-size: px(16);
            padding: px(16) px(24);
          }

          &.deny {
            border-color: $white;
            color: $white;

            @include hover {
              background-color: $white;
              color: $black;
            }
          }
        }
      }

      .fine-print {
        p {
          @include roboto-regular(px(12));
          color: $grey-3;
          margin: 0;
        }
      }
    }

    &.active {
      pointer-events: auto;

      .background {
        pointer-events: auto;
        opacity: 0.9;
      }

      .panel {
        display: block;
        pointer-events: auto;
      }
    }
  }
</style>
